/**
 * Derived ship stats (plan §6 M2-05, spec §8): modules compose multipliers on the pristine `ships.json` def into a
 * new runtime def; the source data is never mutated. Also the derived weapon defs, the tool stats the career
 * mirror carries and the outfitting stat-diff preview. Not per-frame code (runs on fit changes and loads).
 */
import { moduleStats, powerBudget, powerDraw, resolveModule } from '../data/m2'
import { SLOT_IDS, isModuleId, type M2Data, type ModuleId, type SlotId } from '../data/m2Schemas'
import type { ShipDef, WeaponDef, WeaponsData } from '../data/schemas'
import { traitMults } from '../hulks/flaws'
import { toolStatsForFit, type ToolStats } from '../space/contracts'
import { holdCapacityT, type HullFitData } from './hullFit'
import { fail, ok, type CmdResult } from './result'
import type { Career } from './types'

type Fit = Readonly<Record<SlotId, ModuleId>>

/**
 * A new ship def from the pristine `base` and a fit: thrust × `thrustMult` on all four axes, `maxSpeed +
 * maxSpeedAdd`, `absMaxSpeed` raised by `maxSpeedAdd × boostSpeedMult` (so `maxSpeed × boostSpeedMult ≤
 * absMaxSpeed` still holds), shield and regen × the shield stats, and the PDC and pulse-laser mounts remapped to
 * the derived weapon indices (`weaponIndex`, where the runtime appended `deriveWeapons`). Torpedo mounts keep
 * their weapon. `base` is deep-copied, never changed.
 *
 * M9 CH-9a: `flaws` is the flown hull's bitmask (`OwnedShip.flaws`, `hulks/flaws.ts`). After the modules, each flaw
 * scales its one stat, then the First Rush edge (if any) its own (`traitMults` order); `absMaxSpeed` is raised if a
 * multiplier would break `maxSpeed × boostSpeedMult ≤ absMaxSpeed`. 0 (the default) skips this entirely, so a ship
 * with no flaws derives exactly as before.
 */
export function deriveShipDef(
  base: ShipDef,
  fitted: Fit,
  data: Pick<M2Data, 'modules'>,
  weaponIndex: { pdc: number; laser: number },
  flaws = 0,
): ShipDef {
  const def = structuredClone(base)
  const thrusters = moduleStats(data, 'thrusters', fitted.thrusters)
  const shield = moduleStats(data, 'shield', fitted.shield)
  def.thrustForward *= thrusters.thrustMult
  def.thrustReverse *= thrusters.thrustMult
  def.thrustLateral *= thrusters.thrustMult
  def.thrustVertical *= thrusters.thrustMult
  def.maxSpeed += thrusters.maxSpeedAdd
  def.absMaxSpeed += thrusters.maxSpeedAdd * def.boostSpeedMult
  def.shield *= shield.shieldMult
  def.shieldRegen *= shield.regenMult
  const basePdc = data.modules.weaponIndex.pdc
  const baseLaser = data.modules.weaponIndex.laser
  for (const mount of def.mounts) {
    if (mount.weapon === basePdc) mount.weapon = weaponIndex.pdc
    else if (mount.weapon === baseLaser) mount.weapon = weaponIndex.laser
  }
  if (flaws !== 0) {
    for (const m of traitMults(flaws)) def[m.stat] *= m.mult
    def.absMaxSpeed = Math.max(def.absMaxSpeed, def.maxSpeed * def.boostSpeedMult)
  }
  return def
}

/**
 * Copies of the PDC and pulse-laser weapon defs with `damage × damageMult` of the fitted weapon modules. The
 * runtime appends them to the sim's weapon list once and updates them in place on later fit changes.
 */
export function deriveWeapons(baseWeapons: Pick<WeaponsData, 'weapons'>, fitted: Fit, data: Pick<M2Data, 'modules'>): { pdc: WeaponDef; laser: WeaponDef } {
  const pdc = structuredClone(baseWeapons.weapons[data.modules.weaponIndex.pdc])
  const laser = structuredClone(baseWeapons.weapons[data.modules.weaponIndex.laser])
  pdc.damage *= moduleStats(data, 'pdc', fitted.weapon1).damageMult
  laser.damage *= moduleStats(data, 'laser', fitted.weapon2).damageMult
  return { pdc, laser }
}

/** The mirror's tool stats for a fit (the same values as `toolStatsForFit` in the space contracts). */
export function deriveToolStats(fitted: Fit, data: Pick<M2Data, 'modules'>): ToolStats {
  return toolStatsForFit(data, fitted)
}

/** Every stat the outfitting preview compares. */
export const FIT_STAT_KEYS = [
  'powerDraw',
  'powerBudget',
  'pdcDamageMult',
  'laserDamageMult',
  'laserMaxPower',
  'heatCapacityMult',
  'chipRateMult',
  'maxTier',
  'cutMult',
  'spillPerSec',
  'tractorRange',
  'cargoCapacityT',
  'maxPartMassT',
  'shieldMult',
  'regenMult',
  'thrustMult',
  'maxSpeedAdd',
  'scannerRange',
  'scannerMk',
] as const
export type FitStatKey = (typeof FIT_STAT_KEYS)[number]

/** Labels and direction for the preview (`higherIsBetter: false` = a drop is an improvement). */
export const FIT_STAT_META: Readonly<Record<FitStatKey, { label: string; higherIsBetter: boolean }>> = {
  powerDraw: { label: 'Power draw', higherIsBetter: false },
  powerBudget: { label: 'Power budget', higherIsBetter: true },
  pdcDamageMult: { label: 'PDC damage', higherIsBetter: true },
  laserDamageMult: { label: 'Pulse laser damage', higherIsBetter: true },
  laserMaxPower: { label: 'Mining laser max power', higherIsBetter: true },
  heatCapacityMult: { label: 'Mining heat capacity', higherIsBetter: true },
  chipRateMult: { label: 'Chip rate', higherIsBetter: true },
  maxTier: { label: 'Fracture tier', higherIsBetter: true },
  cutMult: { label: 'Cut time', higherIsBetter: false },
  spillPerSec: { label: 'Spill per second', higherIsBetter: false },
  tractorRange: { label: 'Tractor range (m)', higherIsBetter: true },
  cargoCapacityT: { label: 'Cargo capacity (t)', higherIsBetter: true },
  maxPartMassT: { label: 'Largest part (t)', higherIsBetter: true },
  shieldMult: { label: 'Shield strength', higherIsBetter: true },
  regenMult: { label: 'Shield regen', higherIsBetter: true },
  thrustMult: { label: 'Thrust', higherIsBetter: true },
  maxSpeedAdd: { label: 'Top speed bonus (m/s)', higherIsBetter: true },
  scannerRange: { label: 'Scanner range (m)', higherIsBetter: true },
  scannerMk: { label: 'Scanner accuracy (Mk)', higherIsBetter: true },
}

/**
 * The preview's stat values of a fit. M10 P5a4a: with `defId`, the cargo capacity is the hull-rated hold
 * (`career/hullFit.ts holdCapacityT`: the hull's `fit.holdT` × the band's Mk fraction, or the racks' own
 * capacity when the hull has no `fit`, i.e. today's number for every shipped row). Without `defId`, the racks'.
 */
export function fitStats(fitted: Fit, data: Pick<M2Data, 'modules'>, defId?: string, hulls?: HullFitData): Record<FitStatKey, number> {
  const tools = toolStatsForFit(data, fitted)
  const cargo = moduleStats(data, 'cargo', fitted.cargo)
  const shield = moduleStats(data, 'shield', fitted.shield)
  const thrusters = moduleStats(data, 'thrusters', fitted.thrusters)
  return {
    powerDraw: powerDraw(data, fitted),
    powerBudget: powerBudget(data, fitted),
    pdcDamageMult: moduleStats(data, 'pdc', fitted.weapon1).damageMult,
    laserDamageMult: moduleStats(data, 'laser', fitted.weapon2).damageMult,
    laserMaxPower: tools.laserMaxPower,
    heatCapacityMult: tools.heatCapacityMult,
    chipRateMult: tools.chipRateMult,
    maxTier: tools.maxTier,
    cutMult: tools.cutMult,
    spillPerSec: tools.spillPerSec,
    tractorRange: tools.tractorRange,
    cargoCapacityT: defId === undefined ? cargo.capacityT : holdCapacityT(defId, fitted, data, hulls),
    maxPartMassT: cargo.maxPartMassT,
    shieldMult: shield.shieldMult,
    regenMult: shield.regenMult,
    thrustMult: thrusters.thrustMult,
    maxSpeedAdd: thrusters.maxSpeedAdd,
    scannerRange: tools.scannerRange,
    scannerMk: tools.scannerMk,
  }
}

/** One changed stat in the outfitting preview. */
export interface StatDiff {
  stat: FitStatKey
  label: string
  before: number
  after: number
  /** The change is an improvement. */
  better: boolean
}

/**
 * The outfitting preview: every stat that changes when `moduleId` replaces what `slot` holds (before, after), in
 * `FIT_STAT_KEYS` order. Power rows appear whenever the draw or budget changes, so the UI can flag a fit over
 * budget before `fitModule` refuses it. Refuses a module the slot does not take (`slotMismatch`).
 */
export function statDiff(career: Career, data: Pick<M2Data, 'modules'>, slot: SlotId, moduleId: ModuleId): CmdResult<StatDiff[]> {
  if (!isModuleId(moduleId) || !SLOT_IDS.includes(slot)) {
    return fail('slotMismatch', `Unknown slot or module: ${String(slot)}, ${String(moduleId)}.`, { slot: String(slot), module: String(moduleId) })
  }
  const m = resolveModule(data, moduleId)
  if (!data.modules.slots[slot].includes(m.kind)) {
    return fail('slotMismatch', `${m.name} does not fit the ${slot} slot.`, { slot, module: moduleId })
  }
  const before = fitStats(career.ship.fitted, data, career.ship.defId)
  const after = fitStats({ ...career.ship.fitted, [slot]: moduleId }, data, career.ship.defId)
  const rows: StatDiff[] = []
  for (const stat of FIT_STAT_KEYS) {
    const b = before[stat]
    const a = after[stat]
    if (a === b) continue
    const meta = FIT_STAT_META[stat]
    rows.push({ stat, label: meta.label, before: b, after: a, better: meta.higherIsBetter ? a > b : a < b })
  }
  return ok(rows)
}
